import uuid from 'uuid-random';
import axios, { AxiosHeaders } from 'axios';
import {
  AWSImage,
  LaterExemplar,
  EmergentContentSection,
  EmergentExemplar,
  ExemplarWord,
  IImage,
  ILabel,
  Image,
  LanguageFeatureLabel,
  Proforma,
  ProformaContent,
  ProformaImageSection,
  ProformaLine,
  ProformaSectionLabel,
  ProformaSideSection,
  SavedLabel,
  Section,
  SectionColor,
  Word,
  WordsAndSentencesContent,
  WordsPageContent,
  SectionLabel,
  SectionsColors,
  IPosition,
  ProformaLanguageLabel,
} from '../types';
import { navigate, WindowLocation } from '@reach/router';
import {
  COLORS,
  EXEMPLAR_MODES,
  NOT_FOUND,
  PHRASES,
  PREPOSITIONAL_PHRASES,
  SECTIONS_COLORS,
  STYLES,
} from '../constants';
import { BookLabelName } from '../types/exemplars';
import {
  ConsolidatingAIcon,
  ConsolidatingBIcon,
  DevelopingAIcon,
  DevelopingBIcon,
  EarlyAIcon,
  EarlyBIcon,
  EmergentAIcon,
  EmergentBIcon,
  ExtendingAIcon,
  ExtendingBIcon,
} from '../assets/icons/LessonTypes';
import { A, B } from '../assets/icons/Exemplar';
import { authAPI } from '../API/auth';
import { computeNavItems, getLessonIcon, getLessonIconTooltipContent } from './lessons';
import {
  computeSavedNotes,
  computeSavedPenLines,
  DEFAULT_STATE,
  EDIT_TOOL,
  getEditedContent,
  getEditIcon,
  getEditLabel,
  notAvailableTools,
} from './editTool';
import { getStageColors, getAppropriateExemplarRoutes } from './exemplar';
import { parseWordsAndSymbols } from './textEditor/common';
import { assetsApi } from '../API/assets';
import { SENTENCES, WORDS, WORDS_AND_SENTENCES } from '../constants/exemplar';
import {
  SENTENCES as SENTENCES_URL,
  WORDS as WORDS_URL,
  WORDS_AND_SENTENCES as WORDS_AND_SENTENCES_URL,
} from '../constants/navigation';

const options = {
  baseURL: process.env.REACT_APP_API_URL,
  crossDomain: true,
  headers: {
    'Content-Type': 'application/json',
  },
};

export const Axios = axios.create(options);

export const AxiosPrivate = axios.create(options);

// Request interceptor for API calls for AxiosPrivate
AxiosPrivate.interceptors.request.use(
  async config => {
    const token = localStorage.getItem('token');
    config.headers = new AxiosHeaders({
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    });
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Request interceptor for API calls for Axios
Axios.interceptors.request.use(
  async config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = new AxiosHeaders({
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      });
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for API calls
AxiosPrivate.interceptors.response.use(
  response => {
    return response;
  },
  async function (error) {
    const originalRequest = error.config;

    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const access_token = await authAPI.refresh();
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        return AxiosPrivate(originalRequest);
      } catch (error) {
        // @ts-ignore
        return Promise.reject(error.response);
      }
    }

    if (error.response.status === 404) {
      return navigate(`/${NOT_FOUND}`);
    }

    return Promise.reject(error.response);
  }
);

export const getPath = (location: WindowLocation<unknown> | undefined): string => {
  return location?.pathname.split('/')[2] || 'default';
};

export const getLocationPath = (path: string): string[] => {
  return path.split('/').filter(pathItem => pathItem);
};

export const getColorByPartOfSpeech = (language?: string): string => {
  const speech = language?.toLowerCase() || 'default';

  return speech.includes('(') ? COLORS[speech.split(' (')[0]] : COLORS[speech];
};

export const getLanguageFeatureLabels = (label?: string): string => {
  return label || 'Not Found';
};

export const getLanguageFeatureTextContent = (label: string): string => {
  const verbs = /verbs/m;
  if (verbs.test(label)) {
    return label.replace(verbs, 'verb');
  }

  const conjunctions = /Conjunctions/m;
  if (conjunctions.test(label)) {
    let replacedLabel = label.replace(conjunctions, 'Conjunction');
    const textConnectives = /Text connectives/m;
    if (textConnectives.test(label)) {
      replacedLabel = replacedLabel.replace(textConnectives, 'Text connective');
    }

    return replacedLabel;
  }

  const notRemovalSymbols = /\)$/m;
  const labelWithoutLastSymbol = label.slice(0, -1);
  return notRemovalSymbols.test(label) ? label : labelWithoutLastSymbol;
};

export const getLanguageFeatureButtonBorder = (label: string): string | undefined => {
  if (label === PREPOSITIONAL_PHRASES) {
    return 'dotted';
  }

  if (label === PHRASES) {
    return 'solid';
  }

  return undefined; 
};


export const getColorBySectionIndex = (length: number, index: number): SectionColor => {
  switch (index) {
    case 0:
      return SECTIONS_COLORS.teal;
    case 1:
      return SECTIONS_COLORS.orange;
    case length - 1:
      return SECTIONS_COLORS.red;
    default:
      return SECTIONS_COLORS.blue;
  }
};

export const computeSectionsColors = (
  sectionLabels: Array<SectionLabel | ProformaSectionLabel> = []
): SectionsColors => {
  return sectionLabels.reduce((totalSectionsColors: SectionsColors, { label }, index) => {
    return {
      ...totalSectionsColors,
      [label]: getColorBySectionIndex(sectionLabels.length, index),
    };
  }, {});
};

export const getSectionColors = (label?: string, sectionsColors?: Record<string, SectionColor>): SectionColor => {
  const defaultColors: SectionColor = {
    title: '',
    text: '',
  };

  if (!sectionsColors || !label || !sectionsColors[label]) {
    return defaultColors;
  }

  return sectionsColors[label];
};

export const getSections = (entities: Array<Section | Image> = []): Array<Section> =>
  entities.filter(({ type }) => type === 'section') as Section[];

export const getEmergentSections = (
  entities: Array<EmergentContentSection | Image> = []
): Array<EmergentContentSection> => entities.filter(({ type }) => type === 'section') as EmergentContentSection[];

export const getBounds = (
  node: HTMLElement,
  parentNode: HTMLElement
): { left: number; right: number; bottom: number; top: number } => {
  node.style.zIndex = '40';
  if (parentNode) {
    const left = node.offsetWidth / 2;
    const right = parentNode.offsetWidth - node.offsetWidth / 2;
    const top = node.offsetHeight / 2;
    const bottom = parentNode.offsetHeight - node.offsetHeight / 2;

    return { left, right, top, bottom };
  }
  return { left: 0, right: 0, top: 0, bottom: 0 };
};

export const getProportion = (x: number, y: number): number => {
  return (x * 100) / y;
};

export enum Axis {
  x = 'x',
  y = 'y',
}

const getSpaceForAxis = (axis: Axis): number =>
  ({
    [Axis.x]: 21,
    [Axis.y]: 20,
  })[axis];

export const getPixelsFromPercents = (
  absolute: number,
  fraction: number,
  containerSize?: number,
  axis?: Axis
): number => {
  if (containerSize && axis && containerSize > 0 && fraction === 0) {
    const spaceFromBorder = getSpaceForAxis(axis);

    if (axis === Axis.x) {
      return absolute - containerSize / 2 - spaceFromBorder;
    } else {
      return containerSize / 2 + spaceFromBorder;
    }
  }
  return (absolute / 100) * fraction;
};

export const getCollidedLabel = (
  labelPosition: IPosition,
  collisionElements: Array<IImage>,
  screenSize: IPosition
): boolean => {
  let isCollided = false;
  const { x, y } = screenSize;
  const labelOffsetTop = getPixelsFromPercents(labelPosition.y, y);
  const labelOffsetLeft = getPixelsFromPercents(labelPosition.x, x);

  collisionElements.forEach(element => {
    const offsetLeft = getPixelsFromPercents(element.position.x, x);
    const offsetTop = getPixelsFromPercents(element.position.y, y);
    const offsetWidth = getPixelsFromPercents(element.position.width, x);
    const offsetHeight = getPixelsFromPercents(element.position.height, y);

    if (
      labelOffsetLeft < offsetLeft + offsetWidth &&
      labelOffsetLeft > offsetLeft &&
      labelOffsetTop < offsetTop + offsetHeight &&
      labelOffsetTop > offsetTop &&
      element.isHidden
    ) {
      isCollided = true;
    }
  });
  return isCollided;
};

export const getImagesToShow = (images: IImage[] = []): IImage[] => {
  return images.map((image: IImage, index: number) => {
    const isHidden = image.isHidden !== undefined ? image.isHidden : index > 0;
    return { ...image, id: index, isHidden };
  });
};

export const getLabelsToShow = (
  labels: Array<ILabel | SavedLabel>,
  imagesToShow: Array<IImage>,
  x: number,
  y: number
): Array<ILabel | SavedLabel> => {
  return labels
    .map((label, index) => {
      return {
        ...label,
        id: index,
        isHidden: label.isHidden || (label.position ? getCollidedLabel(label.position, imagesToShow, { x, y }) : false),
        isManuallyHidden: label.isManuallyHidden || false,
        isHighlighted: false,
        selectionRange: { width: getVerbWidth(label.text) },
      };
    })
    .filter(label => label.position);
};

export const getCursor = (isInMove: boolean): string => {
  return isInMove ? 'grabbing' : 'auto';
};

export const isTouchDevice = (): boolean => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /android|iphone|ipad/.test(userAgent);
};

export const getProforma = (): boolean =>
  Boolean(window.location.pathname.split('/').includes('interactive-writing-proforma'));

export const getHelpText = (label: string, sectionLabels: Array<ProformaSectionLabel>): string => {
  const result = sectionLabels.find(currentLabel => {
    return currentLabel.label === label;
  });

  return result?.help_text || '';
};

export const getLabelTextColor = (stageName: BookLabelName): string => {
  const labelColors = {
    'Emergent A': COLORS.white,
    'Emergent B': COLORS.debianRed,
    'Early A': COLORS.white,
    'Early B': COLORS.cadmiumViolet,
    'Developing A': COLORS.white,
    'Developing B': COLORS.teal,
    'Consolidating A': COLORS.white,
    'Consolidating B': COLORS.bahamaBlue,
    'Extending A': COLORS.white,
    'Extending B': COLORS.funGreen,
    default: '',
  };

  return labelColors[stageName] ? labelColors[stageName] : labelColors.default;
};

export const getLabelBgColor = (stageName: BookLabelName): string => {
  const labelColors = {
    'Emergent A': COLORS.ABG,
    'Emergent B': COLORS.pinkLace,
    'Early A': COLORS.cadmiumViolet,
    'Early B': COLORS.snuff,
    'Developing A': COLORS.metallicSeaweed,
    'Developing B': COLORS.water,
    'Consolidating A': COLORS.formBlue,
    'Consolidating B': COLORS.lightBlue,
    'Extending A': COLORS.philippineGreen,
    'Extending B': COLORS.caper,
    default: '',
  };

  return labelColors[stageName] ? labelColors[stageName] : labelColors.default;
};

export const getTopLineBgColor = (stageName: BookLabelName): string[] => {
  const labelColors = {
    'Emergent A': [COLORS.ABG, COLORS.brilliantRose, COLORS.illusion],
    'Emergent B': [COLORS.ABG, COLORS.brilliantRose, COLORS.illusion],
    'Early A': [COLORS.eminence, COLORS.cadmiumViolet, COLORS.lavenderPurple],
    'Early B': [COLORS.eminence, COLORS.cadmiumViolet, COLORS.lavenderPurple],
    'Developing A': [COLORS.metallicSeaweed, COLORS.metallicSeaweed, COLORS.metallicSeaweed],
    'Developing B': [COLORS.metallicSeaweed, COLORS.metallicSeaweed, COLORS.metallicSeaweed],
    'Consolidating A': [COLORS.formBlue, COLORS.formBlue, COLORS.formBlue],
    'Consolidating B': [COLORS.formBlue, COLORS.formBlue, COLORS.formBlue],
    'Extending A': [COLORS.philippineGreen, COLORS.philippineGreen, COLORS.philippineGreen],
    'Extending B': [COLORS.philippineGreen, COLORS.philippineGreen, COLORS.philippineGreen],
    default: [''],
  };

  return labelColors[stageName] ? labelColors[stageName] : labelColors.default;
};

export const getLabelLetterIcon = (stageName: BookLabelName): React.FC => {
  const labelIcons = {
    'Emergent A': EmergentAIcon,
    'Emergent B': EmergentBIcon,
    'Early A': EarlyAIcon,
    'Early B': EarlyBIcon,
    'Developing A': DevelopingAIcon,
    'Developing B': DevelopingBIcon,
    'Consolidating A': ConsolidatingAIcon,
    'Consolidating B': ConsolidatingBIcon,
    'Extending A': ExtendingAIcon,
    'Extending B': ExtendingBIcon,
    default: () => null,
  };

  return labelIcons[stageName] ? labelIcons[stageName] : labelIcons.default;
};

// eslint-disable-next-line complexity
export const getCollectionIcon = (stageName: BookLabelName): JSX.Element => {
  switch (stageName) {
    case 'Early A':
      return A(COLORS.cadmiumViolet, COLORS.white);
    case 'Early B':
      return B(COLORS.snuff, COLORS.cadmiumViolet);
    case 'Emergent A':
      return A(COLORS.ABG, COLORS.white);
    case 'Emergent B':
      return B(COLORS.pinkLace, COLORS.debianRed);
    case 'Developing A':
      return A(COLORS.metallicSeaweed, COLORS.white);
    case 'Developing B':
      return B(COLORS.water, COLORS.teal);
    case 'Consolidating A':
      return A(COLORS.formBlue, COLORS.white);
    case 'Consolidating B':
      return B(COLORS.lightBlue, COLORS.bahamaBlue);
    case 'Extending A':
      return A(COLORS.philippineGreen, COLORS.white);
    case 'Extending B':
      return B(COLORS.caper, COLORS.philippineGreen);
    default:
      return A(COLORS.white, COLORS.white);
  }
};

export const getStyles = (styles: string[] | undefined): string => {
  return (
    styles?.reduce<string>((stylesString: string, currentStyle: string) => {
      return (stylesString += STYLES[currentStyle]);
    }, '') || ''
  );
};

export const getLaterStateWord = (exemplarState: LaterExemplar, word: ExemplarWord): ExemplarWord => {
  let side = exemplarState.content.left;
  if (word.side === 'right') {
    side = exemplarState.content.right;
  }
  const section = side[word.sectionIndex] as Section;

  return section.lines[word.lineIndex].words[word.wordIndex];
};

export const getLanguageFeaturesToolMargin = (
  language_feature_labels: LanguageFeatureLabel[],
  index: number
): string => {
  let margin = '0px 5px 0px 5px';
  if (index == 0) {
    margin = '0px 5px 0px 0px';
  }

  if (index == language_feature_labels.length - 1) {
    margin = '0px 0px 0px 5px';
  }

  return margin;
};

export const getExemplarImageURL = (imageId: string, images: AWSImage[]): string => {
  const image = images.find(({ uuid }) => uuid === imageId);
  return image?.url || '';
};

type MultipleIWPWordsSuggestion = {
  wordsArray: string[];
  label: string;
};

const getMultipleWordsSuggestionObjects = (
  languageFeatureLabels: ProformaLanguageLabel[]
): MultipleIWPWordsSuggestion[] => {
  const multipleWordsSuggestion: { word: string; label: string }[] = [];

  languageFeatureLabels
    .map(labelSuggestion => {
      const multipleWordsSuggestions = labelSuggestion.words.filter(suggestion => suggestion.split(' ').length > 1);

      return { ...labelSuggestion, words: multipleWordsSuggestions };
    })
    .filter(({ words }) => words.length)
    .map(({ words, label }) => ({ words: words.map(word => ({ word: word.toLowerCase(), label })) }))
    .forEach(suggestion => {
      multipleWordsSuggestion.push(...suggestion.words);
    });

  return multipleWordsSuggestion
    .map(({ word, label }) => ({ wordsArray: word.split(' '), label }))
    .sort((prev, next) => next.wordsArray.length - prev.wordsArray.length);
};

export const getNewProformaFromState = (
  value: Record<string, string>,
  images: Record<string, string>,
  defaultImages: AWSImage[],
  proforma: Proforma,
  isExemplar: boolean
): ProformaContent => {
  const multipleWordsIWPSuggestions = getMultipleWordsSuggestionObjects(proforma.language_feature_labels);

  const newContent = Object.entries(proforma.content).reduce(
    (content, [sectionName, sectionContent]) => {
      return {
        ...content,
        [sectionName]: sectionContent.map((sectionItem, sectionIndex) => {
          const { id: sectionId, ...sectionItemProps } = sectionItem;

          const getSectionWithAppropriateLines = (section: string) =>
            section.split(/\r|\r\n|\n/).map(line => line.trim().replace(/\s\s+/g, ' '));

          if (sectionItem.type === 'image') {
            const { uuid: imageUuid, isPlaceholder, ...sectionImage } = sectionItemProps as ProformaImageSection;
            const proformaImageUuid = images[`${sectionName}-${sectionIndex}`];
            const isPlaceholderImage =
              !uuid.test(imageUuid as string) &&
              !defaultImages.map(({ uuid: defaultImageUuid }) => defaultImageUuid).includes(proformaImageUuid);

            return {
              ...sectionImage,
              ...(isPlaceholderImage ? { isPlaceholder: true } : { uuid: proformaImageUuid }),
              editable: true,
            };
          }

          const { label: sectionLabel } = sectionItem as ProformaSideSection;

          const proformaSection = value[`${sectionName}-${sectionIndex}`];

          const proformaLines = proformaSection && getSectionWithAppropriateLines(proformaSection);

          const modifiedProformaSection: ProformaSideSection = {
            ...sectionItemProps,
            lines: (sectionItem as ProformaSideSection).lines?.map((line, lineIndex) => {
              const { id: lineId, ...lineProps } = line;

              let currentOffset = 0;

              const getProformaLineWords = () => {
                if (!(proformaLines && proformaLines[lineIndex])) {
                  return [];
                }

                return [...parseWordsAndSymbols(proformaLines[lineIndex])]
                  .filter(word => {
                    return !(word === ' ' && isExemplar);
                  })
                  .map((word, wordIndex) => {
                    const currentProformaLine = proformaLines[lineIndex];
                    const currentOffsetEnd = currentOffset + word.length - 1;

                    const wordText = isExemplar ? word.replace(/(.{22})/g, '$1\n') : word;

                    const newWord: Word = { text: wordText, lineIndex };

                    if (wordIndex > 0 && currentProformaLine[currentOffset - 1] !== ' ') {
                      newWord.style = ['nospacing'];
                    }

                    currentOffset += word.length + (currentProformaLine[currentOffsetEnd + 1] === ' ' ? 1 : 0);

                    return newWord;
                  });
              };

              const proformaLineWords = getProformaLineWords();

              const defaultWords = !proformaLines || lineIndex > proformaLines.length - 1 ? [] : [{ text: '' }];

              return {
                ...lineProps,
                words: proformaLineWords.length ? proformaLineWords : defaultWords,
              };
            }),
          };

          const getAllSectionWords = (proformaSection: ProformaSideSection): Word[] => {
            const allSectionWords: Word[] = [];

            proformaSection.lines.forEach(wordLine => {
              wordLine.words.forEach(word => {
                allSectionWords.push(word);
              });
            });

            return allSectionWords;
          };

          const getAllSectionWordsWithLanguage = (
            allWords: Word[],
            multipleWordsIWPSuggestions: MultipleIWPWordsSuggestion[]
          ): Word[] => {
            const sectionWordsWithLanguage: Word[] = [];

            const isMultipleWordSuggestion = (wordsArray: string[], startingIndex: number): boolean =>
              wordsArray.every((word, index) => {
                const currentWord = allWords[startingIndex];
                const nextWord = allWords[startingIndex + index];
                const isTheSameLine = nextWord && currentWord.lineIndex === nextWord.lineIndex;

                return isTheSameLine && word === nextWord.text?.toLowerCase();
              });

            const getWordLanguage = (wortText: string) =>
              proforma.language_feature_labels.find(({ words }) =>
                words.map(languageWord => languageWord.toLowerCase()).includes(wortText.toLowerCase())
              )?.label;

            const processMultipleWordSuggestion = (wordsArray: string[], label: string, startingIndex: number) => {
              const complexSuggestionWordsArray: string[] = [];
              const endIndex = startingIndex + wordsArray.length - 1;

              for (let i = startingIndex; i <= endIndex; i++) {
                complexSuggestionWordsArray.push(allWords[i].text || '');
              }

              return {
                ...allWords[startingIndex],
                text: complexSuggestionWordsArray.join(' '),
                language: label,
              };
            };

            for (let i = 0; i < allWords.length; i++) {
              const sectionWord = allWords[i];
              let isSuggestionFound = false;

              for (let k = 0; k < multipleWordsIWPSuggestions.length; k++) {
                const multipleWordsSuggestion = multipleWordsIWPSuggestions[k];
                const { wordsArray, label } = multipleWordsSuggestion;

                if (sectionWord.text && wordsArray.includes(sectionWord.text.toLowerCase())) {
                  isSuggestionFound = isMultipleWordSuggestion(wordsArray, i);

                  if (isSuggestionFound) {
                    const languageWord = processMultipleWordSuggestion(wordsArray, label, i);

                    sectionWordsWithLanguage.push(languageWord);

                    i = i + wordsArray.length - 1;

                    break;
                  }
                }
              }

              if (!isSuggestionFound) {
                const wordLanguage = sectionWord.text && getWordLanguage(sectionWord.text);

                const wordObject = { ...sectionWord, text: sectionWord.text, lineIndex: sectionWord.lineIndex };

                const languageWordObject = { ...wordObject, language: wordLanguage };

                sectionWordsWithLanguage.push(wordLanguage ? languageWordObject : wordObject);
              }
            }

            return sectionWordsWithLanguage;
          };

          const getUpdatedSectionLines = (words: Word[]): ProformaLine[] => {
            const updatedLines = [
              ...(sectionItemProps as ProformaSideSection).lines.map(() => ({ words: [] as Word[] })),
            ];

            words.forEach(wordObject => {
              const { lineIndex, language, ...word } = wordObject;

              if (lineIndex === undefined) {
                return;
              }

              const withLanguage =
                language && !(language === PREPOSITIONAL_PHRASES || language === PHRASES) && sectionLabel !== 'Title';

              const modifiedWordObject = withLanguage ? { ...word, language } : word;

              updatedLines[lineIndex] = updatedLines[lineIndex]
                ? { words: [...updatedLines[lineIndex].words, modifiedWordObject] }
                : { words: [modifiedWordObject] };
            });

            return updatedLines;
          };

          const allSectionWords = getAllSectionWords(modifiedProformaSection);

          const sectionWordsWithLanguage = getAllSectionWordsWithLanguage(allSectionWords, multipleWordsIWPSuggestions);

          const updatedSectionLines = getUpdatedSectionLines(sectionWordsWithLanguage);

          return { ...modifiedProformaSection, lines: updatedSectionLines };
        }),
      };
    },
    { left: [], right: [] }
  );

  const getExemplarSectionLines = (lines: ProformaLine[]) => {
    let lastNotEmptyLineIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      if (lines[i].words?.filter(({ text }) => text?.trim()).length > 0) {
        lastNotEmptyLineIndex = i;
      }
    }

    return lines.slice(0, lastNotEmptyLineIndex + 1);
  };

  const content = isExemplar
    ? Object.entries(newContent).reduce(
        (result, [key, value]) => ({
          ...result,
          [key]: value.map((section: ProformaSideSection) =>
            section.type === 'section'
              ? {
                  ...section,
                  lines: getExemplarSectionLines(section.lines as ProformaLine[]),
                }
              : section
          ),
        }),
        { left: [], right: [] }
      )
    : newContent;

  return content;
};

export const getVerbWidth = (text: string): string => {
  return `${text.length + 1}ch`;
};

export const findEmergentExemplarTitle = (
  exemplar: EmergentExemplar,
  page: string
): EmergentContentSection | string => {
  const content = exemplar.content.find(({ label }) => label === page);

  if ('content' in (content || {})) {
    const sections = getEmergentSections((content as WordsAndSentencesContent).content.left);
    return sections.find(({ style }) => style?.includes('heading')) || exemplar.title;
  }

  return exemplar.title;
};

export const urlToContentMap: { [index: string]: string } = {
  [WORDS_URL]: WORDS,
  [SENTENCES_URL]: SENTENCES,
  [WORDS_AND_SENTENCES_URL]: WORDS_AND_SENTENCES,
};

export const findEmergentContentByPage = (
  content: EmergentExemplar['content'],
  page: string
): WordsPageContent | WordsAndSentencesContent | undefined => {
  return content.find(({ label }) => {
    return label === urlToContentMap[page];
  });
};

export const convertSectionToString = (section: Section): string => {
  return section.lines
    .reduce((string, line) => `${string}${line.words.reduce((sentence, word) => `${sentence}${word.text} `, '')}`, '')
    .trim();
};

export { computeNavItems, getLessonIcon, getLessonIconTooltipContent };

export const getHighlighted = (exemplar: LaterExemplar, mode: string): Record<string, boolean> => {
  if (mode === EXEMPLAR_MODES.textPresentation) {
    return {};
  }

  const { meta, section_labels, language_feature_labels } = exemplar;
  const { highlightedSectionLabels, highlightedLanguageFeatureLabels } = meta;

  const isTextStructureMode = mode === EXEMPLAR_MODES.textStructure;

  const labelsToReduce = isTextStructureMode ? section_labels : language_feature_labels;
  const highlightedLabels = isTextStructureMode ? highlightedSectionLabels : highlightedLanguageFeatureLabels;

  return labelsToReduce.reduce<Record<string, boolean>>((highlightState, section_label) => {
    const isHighlighted =
      highlightedLabels && section_label.label in highlightedLabels ? highlightedLabels[section_label.label] : true;

    return {
      ...highlightState,
      ...{
        [section_label.label]: isHighlighted,
      },
    };
  }, {});
};

export const downloadFile = (fileUrl: string, fileName: string): void => {
  const url = window.URL.createObjectURL(new Blob([fileUrl]));
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', `${fileName}.pdf`);

  document.body.appendChild(link);

  link.click();
  link.remove();
};

export const getVideoId = (url: string): string => {
  const array = url.split('/');

  return array.slice(-1).pop() as string;
};

export const handleDownloadFile = async (
  fileName: string,
  fileLink: string,
  setIsLoading: (isLoading: boolean) => void,
  onError: () => void
): Promise<void> => {
  setIsLoading(true);
  try {
    await assetsApi.getAsset(fileLink).then(response => {
      downloadFile(response.data, fileName);
    });
  } catch (error) {
    onError();
  } finally {
    setIsLoading(false);
  }
};

const isSpacesOnly = (str: string): boolean => /^\s*$/.test(str);

const isWordUnderlined = (textType: string | undefined, mode: string, isHiddenMode: boolean): boolean => {
  return textType != undefined && !isHiddenMode && mode === EXEMPLAR_MODES.textPresentation;
};

const areParenthesesClosed = (row: string): string[] => {
  const parentheses: string[] = ["'", '"', '`'];

  const parenthesesStack: string[] = [];

  for (let i = 0; i < row.length; i++) {
    if (!parentheses.includes(row[i])) continue;
    if (!parenthesesStack.includes(row[i])) {
      parenthesesStack.push(row[i]);
    } else {
      parenthesesStack.pop();
    }
  }

  return parenthesesStack;
};

// eslint-disable-next-line complexity
const isSpaceBeforeInsertedProformaWord = (row: string): boolean => {
  const lastRowElement = row.slice(-1);

  const openingBraces: string[] = ['{', '[', '('];
  const parentheses: string[] = ["'", '"', '`'];

  if (!lastRowElement || lastRowElement === '\n' || isSpacesOnly(row)) return false;

  if (lastRowElement === ' ' || openingBraces.includes(lastRowElement)) return false;

  if (
    parentheses.includes(lastRowElement) &&
    (!areParenthesesClosed(row).length || areParenthesesClosed(row).slice(-1)[0] !== lastRowElement)
  )
    return true;

  if (parentheses.includes(lastRowElement) && areParenthesesClosed(row).length) return false;

  return true;
};

const isDraggableElementWasMoved = (prevState: IPosition, state: IPosition): boolean => {
  return Math.abs(prevState.x - state.x) > 0.1 || Math.abs(prevState.y - state.y) > 0.1;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const allAreEqual = (values: any[]): boolean => {
  const result = values.every(value => {
    if (value === values[0]) {
      return true;
    }
  });

  return result;
};

const getLangugeToolCoordinates = (wordSpan: HTMLSpanElement): { x: number; y: number } => {
  const { offsetParent, offsetLeft, offsetTop, offsetWidth, offsetHeight } = wordSpan;

  const { offsetLeft: offsetParentLeft, offsetTop: offsetParentTop } = (offsetParent as HTMLDivElement) || {
    offsetLeft: 0,
    offsetTop: 0,
  };

  const x = offsetParentLeft + offsetLeft + offsetWidth / 2;
  const y = offsetParentTop + offsetTop + offsetHeight;

  return { x, y };
};

const isLFControlButtonClicked = (event: MouseEvent): boolean =>
  (event.target as HTMLButtonElement).getAttribute('aria-label') === 'LF CONTROL BUTTON';

export const getHiddenImageId = (default_uuid: string | undefined, uuid: string): string => {
  return default_uuid || uuid;
};

const isMacPlatform = navigator.userAgent.toLowerCase().indexOf('mac') >= 0;

const handleWindowsShortcut = (
  e: KeyboardEvent,
  condition: { isUndo: boolean; isRedo: boolean },
  handler: (e: KeyboardEvent) => void
): void => {
  const { key } = e;
  const { isUndo, isRedo } = condition;

  switch (key) {
    case 'z':
    case 'Z':
      isUndo && handler(e);
      break;
    case 'y':
    case 'Y':
      isRedo && handler(e);
      break;
  }
};

const handleMacShortcut = (
  e: KeyboardEvent,
  condition: { isUndo: boolean; isRedo: boolean },
  handler: (e: KeyboardEvent) => void
): void => {
  const { key, shiftKey } = e;
  const { isUndo, isRedo } = condition;

  switch (key) {
    case 'z':
    case 'Z':
      if (shiftKey) {
        isRedo && handler(e);
      } else {
        isUndo && handler(e);
      }
      break;
    case 'y':
    case 'Y':
      e.preventDefault();
      isRedo && handler(e);
  }
};

const undoRedoShortcutsHandler = (
  e: KeyboardEvent,
  condition: { isUndo: boolean; isRedo: boolean },
  handler: (e: KeyboardEvent) => void
): void => {
  const { ctrlKey, metaKey } = e;

  if (ctrlKey && !isMacPlatform) {
    handleWindowsShortcut(e, condition, handler);
    return;
  }

  if (metaKey) {
    handleMacShortcut(e, condition, handler);
  }
};

export {
  DEFAULT_STATE,
  EDIT_TOOL,
  getEditLabel,
  getEditIcon,
  notAvailableTools,
  computeSavedNotes,
  computeSavedPenLines,
  getEditedContent,
  getStageColors,
  getAppropriateExemplarRoutes,
  isSpacesOnly,
  isWordUnderlined,
  areParenthesesClosed,
  isSpaceBeforeInsertedProformaWord,
  isDraggableElementWasMoved,
  allAreEqual,
  getLangugeToolCoordinates,
  isLFControlButtonClicked,
  handleWindowsShortcut,
  handleMacShortcut,
  undoRedoShortcutsHandler,
  isMacPlatform,
};
